import "m2m-components/m2m-apps-menu"
import "m2m-components/m2m-releases-link"
import "m2m-components/m2m-user-menu"
import {
    AlertProvider,
    Chip,
    GlobalMenu,
    Loading,
    ModalProvider,
    ThemeProvider
} from "matsuri-ui"
import {
    BrowserRouter,
    Link,
    NavLink,
    Navigate,
    Route,
    Routes,
    useLocation
} from "react-router-dom"
import { FeedbackProvider } from "m2m-components/feedback"
import { M2mAuthenticationProvider } from "m2m-components/storage/useM2mAuth"
import { css } from "@emotion/react"
import { datadogLogs } from "@datadog/browser-logs"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import React, { PropsWithChildren, Suspense } from "react"
import SearchIcon from "@mui/icons-material/Search"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import styled from "@emotion/styled"
import { NuqsAdapter } from "nuqs/adapters/react-router/v7"
import { CarryInRoutes } from "./pages/inventory/carryIn/routes"
import { ErrorBoundary } from "./ErrorBoundary"
import { GuestFormRoutes } from "./routes/guestFormRoutes"
import { MainAuthRoutes } from "./routes/mainAuth"
import { MainNoAuth } from "./routes/mainNoAuth"
import { OwnerCalendarRoutes, OwnerLoginRoutes } from "./routes/ownerCalendar"
import { QuickSearchProvider, useQuickSearch } from "./parts/QuickSearch"
import { StoreKeeperRoutes } from "./pages/inventory/storeKeeper/routes"
import { isGreaterScopeThan } from "./domain/authority"
import { parseJWT } from "./helpers/parseJWT"
import { theme } from "./theme"
import { useAuthUser } from "./hooks/useAuthUser"
import { useBlockCreatePlacementEventFeatureFlag } from "./hooks/useBlockCreatePlacementEventByAuthorityFeatureFlag"
import { useCopyToClipboard } from "./helpers/clipboard"
import { useExpiredProprietaryAlertCount } from "./parts/Navbar/useProprietaryAlertCount"
import { useFindUserById } from "./hooks/useFindUserById"
import { useLeaseNeedActionAlertCount } from "./parts/Navbar/useLeaseNeedActionAlertCount"
import { useUnassignedAlertCount } from "./parts/Navbar/useUnassignedAlertCount"
import { usePendingTransactionAlertCount } from "./parts/Navbar/usePendingTransactionAlertCount"
import { ControllButton } from "./parts/Navbar/ControlButton"

const AvailabilityCalendarPage = React.lazy(
    () => import("./pages/AvailabilityCalendarPage")
)
const ExternalCalendarPage = React.lazy(
    () => import("./pages/external/ExternalCalendar")
)
const ExternalAvailabilityCalendarDelegation = React.lazy(
    () => import("./pages/ExternalAvailabilityCalendarDelegationPage")
)
// よく使うので全体で有効にしてしまう
dayjs.extend(relativeTime)

const Main = styled.main`
    margin: 16px auto 60px auto;
    max-width: 1280px;
    padding: 0px 2vw;
`

const AppRoutes = () => {
    const { authenticated } = useAuth()

    // trailing slashを取り除く
    const location = useLocation()
    if (location.pathname.endsWith("/") && location.pathname !== "/") {
        return <Navigate to={location.pathname.slice(0, -1)} />
    }

    return (
        <Routes>
            <Route
                path="/guest/forms/:listingId/*"
                element={
                    <Main>
                        <Suspense fallback={<Loading />}>
                            <GuestFormRoutes />
                        </Suspense>
                    </Main>
                }
            />
            {/* ここだけスネークケースだがこのurlは外部と共有しているため、スネークケースからキャメルケースに変えられない */}
            <Route
                path="/owner_calendar/*"
                element={
                    <>
                        <Main>
                            <Suspense fallback={<Loading />}>
                                <OwnerCalendarRoutes />
                            </Suspense>
                        </Main>
                    </>
                }
            />

            <Route
                path="/ownerLogin"
                element={
                    <>
                        <Main>
                            <Suspense fallback={<Loading />}>
                                <OwnerLoginRoutes />
                            </Suspense>
                        </Main>
                    </>
                }
            />
            {/* URLを知っていれば誰でも見れる外部向けカレンダー(破壊予定) */}
            <Route
                path="/calendar/*"
                element={
                    <main
                        css={css`
                            padding: 0px 2vw;
                            margin: 2ex auto;
                        `}
                    >
                        <Suspense fallback={<Loading />}>
                            <ExternalCalendarPage />
                        </Suspense>
                    </main>
                }
            />
            {/* 新しい外部向けカレンダー */}
            <Route
                path="/externalAvailabilityCalendarDelegation/*"
                element={
                    <main
                        css={css`
                            padding: 0px 2vw;
                            margin: 2ex auto;
                        `}
                    >
                        <Suspense fallback={<Loading />}>
                            {authenticated ? (
                                <ExternalAvailabilityCalendarDelegation />
                            ) : (
                                <Routes>
                                    <Route
                                        path="*"
                                        element={
                                            <Navigate
                                                to={`/login?redirectUri=${encodeURIComponent(
                                                    `${window.location.pathname}${window.location.search}`
                                                )}`}
                                            />
                                        }
                                    />
                                </Routes>
                            )}
                        </Suspense>
                    </main>
                }
            />
            <Route
                path="/availabilityCalendar/*"
                element={
                    <>
                        <main
                            css={css`
                                max-width: 100%;
                                padding: 0px 1vw;
                                margin: 2ex auto;
                            `}
                        >
                            <Suspense fallback={<Loading />}>
                                <AvailabilityCalendarPage />
                            </Suspense>
                        </main>
                    </>
                }
            />
            <Route
                path="/inventory/storekeeper/*"
                element={
                    <>
                        <div
                            css={css`
                                display: flex;
                                justify-content: flex-end;
                                margin: 16px;
                            `}
                        >
                            <Chip>倉庫管理業者としてログイン中</Chip>
                        </div>
                        <Main>
                            <Suspense fallback={<Loading />}>
                                {authenticated ? (
                                    <StoreKeeperRoutes />
                                ) : (
                                    <Routes>
                                        <Route
                                            path="*"
                                            element={
                                                <Navigate
                                                    to={`/login?redirectUri=${encodeURIComponent(
                                                        `${window.location.pathname}${window.location.search}`
                                                    )}`}
                                                />
                                            }
                                        />
                                    </Routes>
                                )}
                            </Suspense>
                        </Main>
                    </>
                }
            />
            <Route
                path="/inventory/carryin/*"
                element={
                    <>
                        <div
                            css={css`
                                display: flex;
                                justify-content: flex-end;
                                margin: 16px;
                            `}
                        >
                            <Chip>配送業者としてログイン中</Chip>
                        </div>
                        <Main>
                            <Suspense fallback={<Loading />}>
                                {authenticated ? (
                                    <CarryInRoutes />
                                ) : (
                                    <Routes>
                                        <Route
                                            path="*"
                                            element={
                                                <Navigate
                                                    to={`/login?redirectUri=${encodeURIComponent(
                                                        `${window.location.pathname}${window.location.search}`
                                                    )}`}
                                                />
                                            }
                                        />
                                    </Routes>
                                )}
                            </Suspense>
                        </Main>
                    </>
                }
            />
            <Route
                path="*"
                element={
                    <>
                        <Main>
                            <ErrorBoundary>
                                <Suspense fallback={<Loading />}>
                                    {authenticated ? (
                                        <MainAuthRoutes />
                                    ) : (
                                        <MainNoAuth />
                                    )}
                                </Suspense>
                            </ErrorBoundary>
                        </Main>
                    </>
                }
            />
        </Routes>
    )
}

export const Providers = ({ children }: PropsWithChildren) => {
    return (
        <ThemeProvider theme={theme}>
            <M2mAuthenticationProvider>
                <AlertProvider>
                    <ModalProvider>
                        <FeedbackProviderWrapper>
                            {children}
                        </FeedbackProviderWrapper>
                    </ModalProvider>
                </AlertProvider>
            </M2mAuthenticationProvider>
        </ThemeProvider>
    )
}

// tokenやuserIdを使うので、ここでwrappしている
export const FeedbackProviderWrapper = ({ children }: PropsWithChildren) => {
    const { token } = useAuth()
    const userId = parseJWT<{ userId: string }>(token)?.userId
    const { data: user } = useFindUserById(token, userId)
    const userName = user?.name
    return (
        <FeedbackProvider
            userId={userId}
            serviceName="m2m-core"
            userName={userName}
            datadogLogs={datadogLogs}
        >
            <QuickSearchProvider>
                <ErrorBoundary>{children}</ErrorBoundary>
            </QuickSearchProvider>
        </FeedbackProvider>
    )
}
const Logo = () => {
    return (
        <Link
            to="/"
            css={css`
                display: contents;
            `}
        >
            <img
                src="/static/images/m2m_Core.png"
                css={css`
                    width: 50px;
                    height: 50px;
                    margin: 4px;
                `}
                alt="M2M Core"
            />
        </Link>
    )
}
export const AppContent = ({ children }: { children?: React.ReactNode }) => {
    const { authenticated, token } = useAuth()
    const payload = useAuthUser(token)
    const { count: unassignedCount } = useUnassignedAlertCount(token)
    const { count: proprietaryCount } = useExpiredProprietaryAlertCount(token)
    const { count: leaseCount } = useLeaseNeedActionAlertCount(token)
    const { count: pendingTransactionCount } =
        usePendingTransactionAlertCount(token)

    const isBlockCreatePlacementEventByAuthority =
        useBlockCreatePlacementEventFeatureFlag()
    const hasInventoryWritePermission = isBlockCreatePlacementEventByAuthority
        ? payload
            ? isGreaterScopeThan(payload.scope.inventory, "write")
            : false
        : true

    useCopyToClipboard({ text: token, label: "JWT", key: "j" })

    return (
        <GlobalMenu
            logo={<Logo />}
            renderInternalLink={({ href, children }) => {
                return <NavLink to={href}>{children}</NavLink>
            }}
            headerItems={
                <React.Fragment>
                    {authenticated && (
                        <React.Fragment>
                            <HeaderSearchButton />
                            <m2m-releases-link product="m2m-core" />
                        </React.Fragment>
                    )}
                    <m2m-apps-menu />
                    <m2m-user-menu />
                </React.Fragment>
            }
            items={
                // 管理者ではない外部ユーザーの場合は、メニューを表示しない
                authenticated && payload && payload?.authority !== "GeneralUser"
                    ? [
                          {
                              group: "クイックアクセス",
                              items: [
                                  {
                                      text: "直近の専有一覧",
                                      href: "/proprietaryList",
                                      count: proprietaryCount
                                  },
                                  {
                                      text: "未割り当ての予約一覧",
                                      href: "/unassigned",
                                      count: unassignedCount
                                  },
                                  {
                                      text: "更新する必要のある賃貸一覧",
                                      href: "/leaseNeedAction",
                                      count: leaseCount
                                  },
                                  {
                                      text: "金額不一致の予約一覧",
                                      href: "/reconciliationList"
                                  }
                              ]
                          },
                          {
                              group: "メインメニュー",
                              items: [
                                  {
                                      href: "/property",
                                      text: "施設管理"
                                  },
                                  {
                                      href: "/availabilityCalendar",
                                      text: "物件カレンダー"
                                  },
                                  {
                                      href: "/reservation",
                                      text: "予約"
                                  },
                                  {
                                      href: "/invoice/search?page=0&categoryType=charge&status=init",
                                      text: "請求"
                                  },
                                  { href: "/pricing", text: "価格調整" },
                                  {
                                      group: "実績",
                                      items: [
                                          {
                                              href: "/achievement/reservationDetail",
                                              text: "予約詳細"
                                          },
                                          {
                                              href: "/achievement/leaseDetail",
                                              text: "賃貸詳細"
                                          },
                                          {
                                              href: "/pendingTransactionList",
                                              text: "承認待ちの明細一覧",
                                              count: pendingTransactionCount
                                          },
                                          {
                                              href: "/achievement",
                                              text: "その他の実績"
                                          }
                                      ]
                                  },
                                  {
                                      group: "資産管理",
                                      items: [
                                          {
                                              href: "/inventory/commodity",
                                              text: "物品一覧"
                                          },
                                          {
                                              href: "/inventory/orders",
                                              text: "発注一覧"
                                          },
                                          {
                                              href: "/inventory/commodityPlacementEvents/others",
                                              text: "その他の設置情報"
                                          },
                                          {
                                              href: "/inventory/shipments",
                                              text: "出荷一覧"
                                          },
                                          {
                                              href: "/inventory/storage",
                                              text: "倉庫一覧"
                                          },
                                          {
                                              href: "/inventory/leaseContracts",
                                              text: "リース契約一覧"
                                          },
                                          {
                                              href: "/inventory/storageStockSnapshots",
                                              text: "棚卸スナップショット"
                                          },
                                          {
                                              href: "/inventory/itemType",
                                              text: "アイテム区分の設定"
                                          },
                                          {
                                              href: "/inventory/import/commodity",
                                              text: "物品のインポート"
                                          },
                                          {
                                              href: "/inventory/import/order",
                                              text: "仕入れのインポート"
                                          },
                                          {
                                              href: "/inventory/import/storage-transfer",
                                              text: "拠点間移動のインポート"
                                          },

                                          hasInventoryWritePermission
                                              ? {
                                                    href: "/inventory/import/placement",
                                                    text: "設置のインポート"
                                                }
                                              : false,
                                          hasInventoryWritePermission
                                              ? {
                                                    href: "/inventory/import/direct-inventory",
                                                    text: "直接物品在庫登録"
                                                }
                                              : false,
                                          {
                                              href: "/inventory/import/placement-for-preset",
                                              text: "バラ設置の作成"
                                          },
                                          {
                                              href: "/inventory/commodityPlacementEvents/trash",
                                              text: "設置の一括廃棄"
                                          },
                                          {
                                              href: "/inventory/import/carryInDelegation",
                                              text: "設営業者設定のインポート"
                                          },
                                          {
                                              href: "/inventory/import/orderAvailableAmount",
                                              text: "紐付け可能数のインポート"
                                          },
                                          {
                                              href: "/inventory/alert/staledPlacement",
                                              text: "設置日超過アラート"
                                          },
                                          import.meta.env.MODE === "development"
                                              ? {
                                                    href: "/inventory/bigquery/listing_bedding",
                                                    text: "寝具構成インポート(開発のみ)"
                                                }
                                              : undefined,
                                          {
                                              href: "/inventory/webhook",
                                              text: "Webhookの登録"
                                          }
                                      ].filter(Boolean)
                                  },
                                  {
                                      group: "オーナー",
                                      items: [
                                          {
                                              href: "/owner",
                                              text: "オーナー一覧"
                                          }
                                      ]
                                  },
                                  import.meta.env.MODE === "development"
                                      ? {
                                            group: "開発モード",
                                            items: [
                                                {
                                                    href: "/devonly/ota",
                                                    text: "OTA予約"
                                                },
                                                {
                                                    href: "/ownerLogin",
                                                    text: "オーナー向けログイン画面"
                                                }
                                            ]
                                        }
                                      : undefined,
                                  {
                                      group: "一括追加ツール",
                                      items: [
                                          {
                                              href: "/importPropertyFromCsv",
                                              text: "施設追加(旧)"
                                          },
                                          {
                                              href: "/importCreatePropertyFromCsv",
                                              text: "施設のみ追加"
                                          },
                                          {
                                              href: "/importRoomTypeFromCsv",
                                              text: "部屋タイプ一括追加"
                                          },
                                          {
                                              href: "/importCreateListingFromCsv",
                                              text: "物件一括新規作成"
                                          },
                                          {
                                              href: "/importLeaseFromCsv",
                                              text: "賃貸一括追加"
                                          },
                                          {
                                              href: "/importProprietaryFromCsv",
                                              text: "専有一括追加"
                                          },
                                          {
                                              href: "/importManualReservationOtaCommitionFeeFromCsv",
                                              text: "手動予約の手数料一括追加"
                                          },
                                          {
                                              href: "/importAdditionalSalesFromCsv",
                                              text: "追加売上一括追加"
                                          },
                                          {
                                              href: "/importListingFromCsv",
                                              text: "部署ごとの物件一括更新"
                                          },
                                          {
                                              href: "/importListingOTARelationFromCsv",
                                              text: "物件ごとのOTA一括更新"
                                          },
                                          {
                                              href: "/importRoomTypeWeekdaySettingsFromCsv",
                                              text: "部屋タイプの曜日別設定一括更新"
                                          }
                                      ]
                                  },
                                  {
                                      group: "その他",
                                      items: [
                                          {
                                              href: "/roomTypeMonitor",
                                              text: "稼働率"
                                          },
                                          {
                                              href: "/airbnbCharge",
                                              text: "入金"
                                          },
                                          {
                                              href: "/pictureSorter",
                                              text: "写真の並び替えツール"
                                          },
                                          import.meta.env.MODE === "development"
                                              ? {
                                                    href: "/editListingPresentationSchema",
                                                    text: "物件スキーマ編集"
                                                }
                                              : undefined,
                                          {
                                              href: "/beds24Auth",
                                              text: "beds24と連携"
                                          },
                                          {
                                              href: "/troubleForm",
                                              text: "トラブル報告フォーム(社内向け)"
                                          },
                                          {
                                              text: "民泊残日数が少ない物件一覧",
                                              href: "/minpakuYearStocks"
                                          },
                                          {
                                              text: "インチェックが必要な物件一覧",
                                              href: "/inCheckListings"
                                          },
                                          {
                                              text: "BigQuery連携",
                                              href: "/bigquery_integration"
                                          }
                                      ].filter(
                                          (
                                              item
                                          ): item is {
                                              text: string
                                              href: string
                                          } => item !== undefined
                                      )
                                  },
                                  {
                                      group: "ドキュメント",
                                      items: [
                                          {
                                              href: "/revenue-recognition",
                                              text: "売上認識"
                                          },
                                          {
                                              href: "/stay-sales-v2",
                                              text: "StaySalesV2"
                                          }
                                      ]
                                  }
                              ].filter(
                                  (
                                      item
                                  ): item is {
                                      text: string
                                      href: string
                                  } => item !== undefined
                              )
                          }
                      ]
                    : []
            }
        >
            {children}
        </GlobalMenu>
    )
}

const HeaderSearchButton = () => {
    const { launch } = useQuickSearch()
    return (
        <ControllButton onClick={launch}>
            <SearchIcon />
        </ControllButton>
    )
}

export const App = () => {
    return (
        <BrowserRouter basename={import.meta.env.BASENAME}>
            <Providers>
                <AppContent>
                    <NuqsAdapter>
                        <AppRoutes />
                    </NuqsAdapter>
                </AppContent>
            </Providers>
        </BrowserRouter>
    )
}
